import { Inject, Injectable } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { map, mergeMap, concatMap, mergeAll, toArray, concatAll, shareReplay, delay, first } from 'rxjs/operators';
import { WachterRadioButtonListComponent, RadioInputSaveResult } from '../../../shared/custom-controls/radio-button-list-control/radio-button-list-control.component';
import { FormControl, FormGroup, AbstractControl } from '@angular/forms';
import { NumbersMetaDataItemNames, OverheadProjectionEntity, NumbersMetaDataLookups } from '../meta-data/numbers-meta-data.service'
import { DwActionResult, DwAction, DwActionType, DwMdFormInitActionHandler, DwMdFormSaveActionResult,
         DwMdFormInitActionState, DwMdFormActionResult, DwMdFormActionHandler, DwMdFormActionState
       } from '@devwareapps/devware-cap';
import { debounceTime } from 'rxjs/operators';


@DwAction({
    keyName: 'OverheadProjections_Create_Multiple',
    display: 'Overhead Projections - Create Multiple',
    actionType: DwActionType.formAction,
    isGlobal: true,
    parentItemName: [NumbersMetaDataItemNames.OverheadProjection],
})
@Injectable()
export class OverheadProjectionsCreateMultipleActionService implements DwMdFormActionHandler, DwMdFormInitActionHandler {
    formGroup: FormGroup;
    phaseId: string = 'PhaseId';
    formInitActionState: DwMdFormInitActionState;

    constructor() { }
    jobNumber: number;
    defaultEntity : OverheadProjectionEntity;
    status : number = ProjectionMultiSaveStatus.Initiating;

    initAction(formInitActionState: DwMdFormInitActionState) {
        this.formInitActionState = formInitActionState;
        this.formGroup = this.formInitActionState.formApi.formGroup;
        this.formInitActionState.formApi.isDataLoadComplete
            .pipe(first()).subscribe(ok => 
                {
                    setTimeout(() => {
                        const defaultEntity = this.formInitActionState.formApi.getFormData();
                        this.defaultEntity = { ...defaultEntity };
                        this.status = ProjectionMultiSaveStatus.InProgress;
                    }, 500);
                });

        this.formGroup.valueChanges.pipe(debounceTime(500))
            .subscribe(value => 
            {
                const ctrl = this.SelectPhasesControl;
                const op: OverheadProjectionEntity = this.formGroup.value;
                const hasJn = ctrl != undefined && op.JobNumberId != undefined;

                if (ctrl != undefined)
                {
                    ctrl.splitColumn = true;
                    ctrl.fontSize = '0.75rem';
                }
                
                if (this.status == ProjectionMultiSaveStatus.SaveFailed){
                    ctrl.saveInProgress = false;
                    ctrl.selectFirstFailure();
                    this.status = ProjectionMultiSaveStatus.InProgress;
                    return;
                }

                if (this.status == ProjectionMultiSaveStatus.SaveCompleted
                    && hasJn == false)
                {
                    this.resetValidationErrors();
                    this.formGroup.patchValue(this.defaultEntity);
                    this.status = ProjectionMultiSaveStatus.PostSaveReloading;
                    return;
                }

                if (this.status == ProjectionMultiSaveStatus.PostSaveReloading 
                         && hasJn == true){
                    this.status = ProjectionMultiSaveStatus.PostSaveReloaded;
                    ctrl.reset();
                }
                else if (this.status == ProjectionMultiSaveStatus.PostSaveReloaded)
                    this.status = ProjectionMultiSaveStatus.InProgress;

                const changedJn = hasJn && this.jobNumber != op.JobNumberId;
                this.jobNumber = op.JobNumberId;

                if (ctrl && !ctrl.setDisplay)
                    ctrl.setDisplay = (obj, display) => 
                    {
                        const cost = obj?.Cost > 0 ? 
                                        `<span class="dw-green">${Number(obj?.Cost).toFixed(2)}</span>` : 
                                        `<span class="dw-red"><b>0.00</b></span>`;
                        return `${display} ($${cost}/${obj?.Hours || '0'} Hrs)`
                    };

                /* Reset list and blank phases if JobNumber changed */
                if (changedJn){
                    ctrl.reset(true);
                    op.Cost = null;
                    op.Hours = null;
                    op.Notes = null;
                    if (ctrl.saveInProgress == false)
                        op.PhaseId = null; 
                    this.formGroup.patchValue(op);
                } else if (hasJn){
                    ctrl.assignLookup(NumbersMetaDataLookups.EthosJobNumberPhaseAllItems, 
                                    [{ ParamName: 'JobNumberId', ParamValue: this.jobNumber.toString() }]);
                    ctrl.isResetting = () => {
                        const op: OverheadProjectionEntity = this.formGroup.value;
                        const hasJn = ctrl != undefined && this.jobNumber != null;
                        const changedJn = (hasJn && this.jobNumber != op.JobNumberId) ||
                                          (this.jobNumber == null && op.JobNumberId > 0);
                        return changedJn;
                    }
                }
            });
    }

    handleAction(formActionState: DwMdFormActionState): Observable<DwActionResult> {
        this.status = ProjectionMultiSaveStatus.SaveAttempted;
        const ctrl = this.SelectPhasesControl;
        ctrl.saveInProgress = true;
        const pendingSaves = ctrl.PendingUpdates;
        const saves: Observable<boolean>[] = [];
        let netResult: boolean = true;

        for (let key in pendingSaves) {
            /* Get an observable and add to collection */
            const task = new Observable<boolean>((observer) =>
            {
                let value = pendingSaves[key];
                const data = value.data;
                
                /* Set PhaseId to single value */
                data.PhaseId = value.id;
                
                /* Temporarily assign data and verify using whatever form validation exists */
                if (data)
                    formActionState.formApi.setFormData(data);

                /* Immediately save */
                const saveResult = formActionState.formApi.saveData(true);
                saveResult.subscribe(saveResult => 
                    {
                        value.ok = saveResult.success;

                        /* Capture status here since we are going to force in order (concatAll) 
                        *  to preserve the integrity of assigning data and saving */
                        netResult = netResult && saveResult.success == true;

                        observer.next(netResult); //Send result to combined observer below
                        observer.complete(); //Mark as done so the subscribe gets the data
                    });
            }).pipe(shareReplay(1));
            saves.push(task);
        }
        /* Combine into single observable and pipe into join processor */
        const saveInProgress = 
            forkJoin(saves).pipe(concatAll())
                        .pipe(mergeMap(netSaveOutcome => {
                                const actionResult: DwMdFormActionResult = { success: netSaveOutcome };
                                return of(actionResult);
                            }));

        saveInProgress.pipe(first())
            .subscribe(actionResult => {
                /* Reset to default AS-IF brand new. This requires resetting after page reloads updated saved data. */
                if (actionResult.success == true)
                    this.formInitActionState.formApi.isDataLoadComplete
                        .pipe(first()).subscribe(ok => 
                            {
                                this.status = ProjectionMultiSaveStatus.SaveCompleted;
                                setTimeout(() => {
                                    this.formGroup.patchValue(null);
                                    this.formGroup.reset();
                                    ctrl.completeSaveInProgress();
                                }, 500);
                            });
                else
                    this.status = ProjectionMultiSaveStatus.SaveFailed;
            });
        return saveInProgress;  
    }
    resetValidationErrors() {
        this.formInitActionState.formApi.clearFormSubmitted();
        Object.keys(this.formGroup.controls).forEach(controlName => {
          const ctrl = this.formGroup.get(controlName);
          ctrl.setErrors(null);
        });
      }

    get SelectPhasesControl(): WachterRadioButtonListComponent  {
        return this.formGroup.get('RadioList_PhaseId') as WachterRadioButtonListComponent;
    }
    get SelectPhases(): number[] {
        const ctrl : AbstractControl = this.SelectPhasesControl;
        return ctrl?.value?.toString().split(",").map(Number);
    }
}

export enum ProjectionMultiSaveStatus {
    Initiating = 0,
    InProgress = 1,
    SaveAttempted = 3,
    SaveCompleted = 4,
    SaveFailed = 5,
    PostSaveReloading = 6,
    PostSaveReloaded = 7
}